import { useEffect, useRef } from "react";

import { useTranslation } from "next-i18next";
import Script from "next/script";

import { type ContentLocale } from "@/i18n/config";
import { useContentLocale } from "@/i18n/hooks";
import { Heading } from "@/ui/cva/Heading";

declare global {
  interface Window {
    Trustpilot?: {
      loadFromElement: (element: HTMLElement, forceReload?: boolean) => void;
    };
  }
}

type TrustpilotWidgetLocale = {
  locale: string;
  reviewLanguages: string;
  host: string;
};

/**
 * ES and IT show English reviews: on 2026-10-27 each had only 4 four- and five-star reviews in its
 * own language, which leaves the slider nearly empty, and Dutch or German ones they can't read.
 */
const TRUSTPILOT_LOCALES: Record<ContentLocale, TrustpilotWidgetLocale> = {
  nl: { locale: "nl-NL", reviewLanguages: "nl", host: "nl.trustpilot.com" },
  en: { locale: "en-GB", reviewLanguages: "en", host: "uk.trustpilot.com" },
  de: { locale: "de-DE", reviewLanguages: "de", host: "de.trustpilot.com" },
  fr: { locale: "fr-FR", reviewLanguages: "fr", host: "fr.trustpilot.com" },
  es: { locale: "es-ES", reviewLanguages: "en", host: "es.trustpilot.com" },
  it: { locale: "it-IT", reviewLanguages: "en", host: "it.trustpilot.com" },
};

/**
 * Reviews about Whoppah itself, under the seller block (WHO-6536). The heading says so, otherwise
 * buyers read them as reviews of this seller. If the script is blocked only the fallback link shows.
 */
export const ProductDetailsTrustpilot = () => {
  const { t } = useTranslation();
  const locale = useContentLocale();
  const widgetRef = useRef<HTMLDivElement>(null);

  const widget = TRUSTPILOT_LOCALES[locale];

  /*
   * The bootstrap script renders every widget on the page once, when it loads. A widget mounted
   * after that (client-side navigation, a language switch) has to be rendered by hand.
   */
  useEffect(() => {
    if (widgetRef.current) {
      window.Trustpilot?.loadFromElement(widgetRef.current, true);
    }
  }, [widget.locale]);

  return (
    <div className="md:col-span-3">
      <Heading as="h4" className="mb-6">
        {t("pdp:pdp-trustpilot-title")}
      </Heading>
      <Script
        src="https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
        strategy="lazyOnload"
      />
      <div
        key={widget.locale}
        ref={widgetRef}
        className="trustpilot-widget h-60"
        data-locale={widget.locale}
        data-template-id="54ad5defc6454f065c28af8b"
        data-businessunit-id="600f03b83d37190001bc18a8"
        data-style-height="240px"
        data-style-width="100%"
        data-stars="4,5"
        data-review-languages={widget.reviewLanguages}
      >
        <a
          href={`https://${widget.host}/review/whoppah.com`}
          target="_blank"
          rel="noopener"
        >
          Trustpilot
        </a>
      </div>
    </div>
  );
};
